import React, { useState, useImperativeHandle, useEffect, useRef } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { DialogOverlay, Dialog } from "components/dialog/DialogOverlay";
import EmptyGridViewAssetsComponent from "./EmptyGridViewAssetsComponent";
import i18n from "locale/i18n";
import { isInternalUserSelector, eventBus, getHashParams } from "@kubera/common";
import { hashParams, modalValues } from "routes";

const Container = styled(DialogOverlay)``;

const AddDialog = styled(Dialog)`
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 674px;
  margin-top: 80px;
  padding: 60px;
  box-sizing: border-box;
  min-height: 614px;
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 22px;
  line-height: 130%;
`;

const AddMenu = styled(EmptyGridViewAssetsComponent)`
  margin-top: 15px;
`;

const AddModal = React.forwardRef(
  (
    {
      isReadOnlyWlClient = false,
      aiImportDisabled = false,
      handleMenuItemClick = () => null,
      isEmpty = false,
      gridData,
      isComingFromEmptyScreenRef
    },
    ref
  ) => {
    const isInternalUser = useSelector(isInternalUserSelector);
    const prevSectionRows = useRef(null);
    const linkingStartedFlag = useRef(null);

    const [sheetData, setSheetData] = useState(null);

    const sectionRows = !sheetData
      ? null
      : (() => {
          const sheet = gridData.sheets[sheetData.sheetIndex];

          if (!sheet) {
            return null;
          }

          const section = sheet.sections[sheetData.sectionIndex];

          if (!section) {
            return null;
          }

          return section.rows.filter(row => !row.isEmpty());
        })();

    const handleTipClick = item => {
      if (!item) {
        setSheetData(null);
      }
      handleMenuItemClick(sheetData.sheetIndex, sheetData.sectionIndex, item);
    };

    const handleCloseModal = (linkingStarted = true) => {
      // Don't allow linking started to go from true to false in case this function is called
      // with false after true then the callback will be called which results in the
      // target section where linking is going on to be deleted in case it was invoked
      // from the buttons at the bottom of the grid
      if (linkingStartedFlag.current === null || linkingStartedFlag.current === false) {
        linkingStartedFlag.current = linkingStarted;
      }

      if (sheetData && sheetData.callback) {
        sheetData.callback(linkingStartedFlag.current);
      }
      setSheetData(null);
      prevSectionRows.current = null;
    };

    useImperativeHandle(ref, () => ({
      isOpen: () => {
        return !!sheetData;
      },
      show: (sheetIndex, sectionIndex, callback) => {
        setSheetData({
          sheetIndex,
          sectionIndex,
          callback
        });
        linkingStartedFlag.current = false;
      },
      close: () => {
        handleCloseModal(false);
      }
    }));

    useEffect(() => {
      const closeModalIfEventMatches = event => {
        const { eventName } = event.detail;

        if (eventName === "addModalClose") {
          handleCloseModal(false);
        }
      };

      eventBus.addEventListener("any", closeModalIfEventMatches);

      return () => {
        eventBus.removeEventListener("any", closeModalIfEventMatches);
      };
    }, []);

    useEffect(() => {
      if (prevSectionRows.current && sectionRows && prevSectionRows.current.length !== sectionRows.length) {
        handleCloseModal(true);
      }

      prevSectionRows.current = sectionRows;
    }, [sectionRows]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (getHashParams(window.location)[hashParams.MODAL] === modalValues.AI_DOCUMENT_IMPORT) {
        return;
      }
      if (!isComingFromEmptyScreenRef.current && isEmpty) {
        setSheetData({
          sheetIndex: 0,
          sectionIndex: 0
        });
      } else {
        handleCloseModal(false);
      }
    }, [isEmpty]); // eslint-disable-line react-hooks/exhaustive-deps

    if (sheetData) {
      return (
        <Container onDismiss={() => handleCloseModal(false)}>
          <AddDialog>
            <Title>{i18n.t("assetsComponent.addViewDialogTitle")}</Title>
            <AddMenu
              isReadOnlyWlClient={isReadOnlyWlClient}
              aiImportDisabled={aiImportDisabled}
              isInternalUser={isInternalUser}
              handleInitialTipClick={handleTipClick}
              isAddNewFlow
            />
          </AddDialog>
        </Container>
      );
    }

    return null;
  }
);

AddModal.close = () => {
  eventBus.dispatch("addModalClose");
};

export default AddModal;
